import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2, X, Clock, Layers, Cpu, Zap, ShieldCheck, TrendingUp } from 'lucide-react';
import { elodge, auth } from '@/lib/api';

const STATUS_TONE = {
  queued: 'bg-slate-100 text-slate-600 border-slate-200',
  running: 'bg-sky-100 text-sky-700 border-sky-200',
  awaiting_human: 'bg-amber-100 text-amber-700 border-amber-200',
  succeeded: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
};

const STATUS_ICON = {
  queued: Clock, running: Loader2, awaiting_human: AlertTriangle,
  succeeded: CheckCircle2, failed: X, cancelled: X,
};

export default function ElodgeFleetPage() {
  const me = auth.currentUser();
  const isSuper = me?.role === 'super_admin';
  const [snap, setSnap] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [reaping, setReaping] = React.useState(false);
  const [reapMsg, setReapMsg] = React.useState(null);
  const [detail, setDetail] = React.useState(null); // {job, token, human_input}
  const [cancelTarget, setCancelTarget] = React.useState(null); // {org_id, active}
  const [slaTrend, setSlaTrend] = React.useState(null);

  const loadTrend = React.useCallback(async () => {
    try { setSlaTrend(await elodge.sla.trend(90)); }
    catch { setSlaTrend({ subclasses: {}, days: 90 }); }
  }, []);

  const load = React.useCallback(async ({ background = false } = {}) => {
    if (!background) setLoading(true);
    try { setSnap(await elodge.fleet()); }
    catch { /* noop */ }
    finally { if (!background) setLoading(false); }
  }, []);

  React.useEffect(() => { if (isSuper) { load(); loadTrend(); } }, [isSuper, load, loadTrend]);

  // Background auto-refresh every 8s — does NOT blank the dashboard
  React.useEffect(() => {
    if (!isSuper) return;
    const iv = setInterval(() => load({ background: true }), 8000);
    return () => clearInterval(iv);
  }, [isSuper, load]);

  const reap = async () => {
    setReaping(true); setReapMsg(null);
    try {
      const r = await elodge.reap();
      setReapMsg({ ok: true, text: `Reaped ${r.reaped} stale job${r.reaped === 1 ? '' : 's'}.` });
      load({ background: true });
    } catch (e) {
      setReapMsg({ ok: false, text: e?.response?.data?.detail || 'Reap failed' });
    } finally {
      setReaping(false);
      setTimeout(() => setReapMsg(null), 4000);
    }
  };

  if (!isSuper) {
    return (
      <div className="p-8">
        <Card className="border-slate-200"><CardContent className="p-6">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
            <ShieldCheck className="h-4 w-4 text-amber-600" /> Restricted
          </div>
          <div className="mt-1 text-xs text-slate-500">Only platform super-admins can view the eLodge fleet.</div>
        </CardContent></Card>
      </div>
    );
  }

  if (loading || !snap) {
    return <div className="flex h-full items-center justify-center p-16"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>;
  }

  const c = snap.counts;
  const KPIS = [
    { k: 'queued', label: 'Queued', icon: Clock, tone: 'text-slate-600' },
    { k: 'running', label: 'Running', icon: Cpu, tone: 'text-sky-700' },
    { k: 'awaiting_human', label: 'Awaiting human', icon: AlertTriangle, tone: 'text-amber-600' },
    { k: 'succeeded', label: 'Succeeded', icon: CheckCircle2, tone: 'text-emerald-700' },
    { k: 'failed', label: 'Failed', icon: X, tone: 'text-red-600' },
  ];

  return (
    <div className="p-6" data-testid="elodge-fleet-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">eLodge Fleet</h1>
          <div className="text-sm text-slate-500">
            Cross-tenant lodgement worker status · concurrency {snap.max_concurrent_per_org}/firm · stale reap ≥ {snap.stale_minutes}m
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="fleet-reap" onClick={reap} disabled={reaping} variant="outline"
                  className="h-9 gap-1 border-amber-200 text-amber-700 text-xs">
            {reaping ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />} Reap stale
          </Button>
          <Button data-testid="fleet-refresh" onClick={load} className="h-9 gap-1 bg-[#1A3A5C] text-white text-xs">
            <RefreshCw className="h-3 w-3" /> Refresh
          </Button>
        </div>
      </div>

      {reapMsg && (
        <div data-testid="fleet-reap-msg"
             className={`mt-3 rounded-md p-2 text-xs ${reapMsg.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          {reapMsg.text}
        </div>
      )}

      {snap.per_subclass_backlog?.some((s) => s.suggest_scale) && (
        <div data-testid="fleet-autoscale-banner"
             className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <div className="flex items-start gap-2">
            <Zap className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <div>
              <div className="font-semibold">Auto-scale suggestion</div>
              <div className="mt-0.5">
                {snap.per_subclass_backlog.filter((s) => s.suggest_scale).map((s) => (
                  <div key={s.subclass} data-testid={`fleet-autoscale-${s.subclass}`} className="mt-1">
                    <span className="font-mono font-semibold">Subclass {s.subclass}</span>
                    <span className="ml-1">has <b>{s.backlog}</b> jobs queued, oldest waiting <b>{s.oldest_wait_minutes}m</b>.</span>
                    <span className="ml-1 text-[10px] text-amber-700">
                      Consider raising <code className="rounded bg-white px-1 py-0.5">numprocs</code> in
                      <code className="ml-1 rounded bg-white px-1 py-0.5">/etc/supervisor/conf.d/elodge_worker.conf</code> for the
                      <code className="ml-1 rounded bg-white px-1 py-0.5">elodge_worker_{s.subclass}</code> pool.
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-5">
        {KPIS.map(({ k, label, icon: Ico, tone }) => (
          <Card key={k} className="border-slate-200"><CardContent className="p-4" data-testid={`fleet-kpi-${k}`}>
            <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest ${tone}`}>
              <Ico className="h-3 w-3" /> {label}
            </div>
            <div className="mt-1 font-serif text-3xl font-bold text-[#1A3A5C]">{c[k] || 0}</div>
          </CardContent></Card>
        ))}
      </div>

      <SlaTrendCard trend={slaTrend} />

      <Card className="mt-6 border-slate-200"><CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
          <Layers className="h-4 w-4 text-[#2ECC9A]" /> Live jobs ({(snap.live || []).length})
        </div>
        {snap.live.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-200 p-6 text-center text-xs text-slate-500">
            Queue is empty. Fleet is idle.
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border border-slate-200">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-left text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Matter</th>
                  <th className="px-3 py-2 font-medium">Subclass</th>
                  <th className="px-3 py-2 font-medium">Attempts</th>
                  <th className="px-3 py-2 font-medium">Mode</th>
                  <th className="px-3 py-2 font-medium">Org</th>
                  <th className="px-3 py-2 font-medium">Updated</th>
                  <th className="px-3 py-2 font-medium" />
                </tr>
              </thead>
              <tbody data-testid="fleet-live-jobs">
                {snap.live.map((j) => {
                  const Ico = STATUS_ICON[j.status] || Clock;
                  const spinning = j.status === 'running';
                  return (
                    <tr key={j.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`fleet-job-${j.id}`}>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] capitalize ${STATUS_TONE[j.status]}`}>
                          <Ico className={`h-3 w-3 ${spinning ? 'animate-spin' : ''}`} />
                          {j.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-slate-700">{j.matter_ref}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-slate-700">{j.subclass}</td>
                      <td className="px-3 py-2 text-slate-600">{j.attempts}/{j.max_attempts}</td>
                      <td className="px-3 py-2 text-slate-600">{j.dry_run ? 'Dry' : 'Live'}</td>
                      <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{(j.org_id || '').slice(0, 8)}…</td>
                      <td className="px-3 py-2 text-[10px] text-slate-500">{new Date(j.updated_at).toLocaleTimeString('en-AU')}</td>
                      <td className="px-3 py-2 text-right">
                        {j.status === 'awaiting_human' && (
                          <Button size="sm" variant="outline"
                                  data-testid={`fleet-resume-${j.id}`}
                                  onClick={() => setDetail({ job: j, token: '', human_input: '' })}
                                  className="h-6 gap-1 border-amber-300 bg-amber-50 text-[10px] text-amber-700">
                            <ShieldCheck className="h-3 w-3" /> Resume
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent></Card>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {snap.per_subclass_backlog?.length > 0 && (
          <Card className="border-slate-200 md:col-span-2"><CardContent className="p-5" data-testid="fleet-per-subclass">
            <div className="text-sm font-semibold text-[#1A3A5C]">Backlog per subclass</div>
            <div className="mt-3 overflow-hidden rounded-md border border-slate-200">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-left text-slate-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Subclass</th>
                    <th className="px-3 py-2 font-medium text-right">Queued</th>
                    <th className="px-3 py-2 font-medium text-right">Running</th>
                    <th className="px-3 py-2 font-medium text-right">Oldest wait</th>
                    <th className="px-3 py-2 font-medium">Recommendation</th>
                  </tr>
                </thead>
                <tbody>
                  {snap.per_subclass_backlog.map((s) => (
                    <tr key={s.subclass} className="border-t border-slate-100" data-testid={`fleet-subclass-${s.subclass}`}>
                      <td className="px-3 py-2 font-mono text-[11px]">{s.subclass}</td>
                      <td className="px-3 py-2 text-right font-semibold">{s.backlog}</td>
                      <td className="px-3 py-2 text-right text-slate-600">{s.running_now}</td>
                      <td className="px-3 py-2 text-right text-slate-600">{s.oldest_wait_minutes}m</td>
                      <td className="px-3 py-2">
                        {s.suggest_scale
                          ? <Badge variant="outline" className="border-amber-300 bg-amber-50 text-[10px] text-amber-800">Scale up</Badge>
                          : <Badge variant="outline" className="border-slate-200 text-[10px] text-slate-500">Healthy</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        )}
        <Card className="border-slate-200"><CardContent className="p-5" data-testid="fleet-per-org-section">
          <div className="text-sm font-semibold text-[#1A3A5C]">Active jobs per firm</div>
          {snap.per_org_active.length === 0 ? (
            <div className="mt-3 text-xs text-slate-500">No active jobs.</div>
          ) : (
            <div className="mt-3 space-y-1" data-testid="fleet-per-org">
              {snap.per_org_active.map((o) => (
                <div key={o.org_id} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-1.5 text-xs">
                  <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-slate-600">{o.org_id.slice(0, 12)}…</span>
                  <span className="font-semibold text-[#1A3A5C]">{o.active}</span>
                  <Button size="sm" variant="outline"
                          data-testid={`fleet-cancel-org-${o.org_id}`}
                          onClick={() => setCancelTarget(o)}
                          className="h-6 gap-1 border-red-200 bg-red-50 text-[10px] text-red-700 hover:bg-red-100">
                    <X className="h-3 w-3" /> Cancel all
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent></Card>

        <Card className="border-slate-200"><CardContent className="p-5">
          <div className="text-sm font-semibold text-[#1A3A5C]">Recent finished (last 20)</div>
          {snap.recent_finished.length === 0 ? (
            <div className="mt-3 text-xs text-slate-500">No finished jobs yet.</div>
          ) : (
            <div className="mt-3 max-h-60 space-y-1 overflow-auto" data-testid="fleet-recent">
              {snap.recent_finished.map((j) => (
                <div key={j.id} className="flex items-center justify-between rounded-md bg-slate-50 px-2 py-1 text-[11px]">
                  <span className="min-w-0 truncate">
                    <span className="font-mono text-slate-600">{j.matter_ref}</span>
                    <span className="ml-1 text-slate-500">· {j.subclass}</span>
                  </span>
                  <Badge variant="outline" className={`capitalize ${STATUS_TONE[j.status]}`}>
                    {j.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent></Card>
      </div>

      <ResumeDialog detail={detail} onClose={() => setDetail(null)} onResumed={load} />
      <CancelOrgDialog target={cancelTarget} onClose={() => setCancelTarget(null)}
                        onCancelled={() => { setCancelTarget(null); load({ background: true }); }} />
    </div>
  );
}


function CancelOrgDialog({ target, onClose, onCancelled }) {
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [ack, setAck] = React.useState(false);
  React.useEffect(() => { if (target) { setReason(''); setErr(''); setAck(false); } }, [target]);
  if (!target) return null;
  const submit = async () => {
    if (!ack) { setErr('Please tick the confirmation box'); return; }
    setBusy(true); setErr('');
    try {
      const r = await elodge.cancelOrg(target.org_id, reason.trim() || undefined);
      onCancelled?.(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Cancel failed');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="fleet-cancel-org-dialog">
        <DialogHeader><DialogTitle>Cancel every active job for this firm?</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md bg-red-50 p-3 text-xs text-red-800">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
              <div>
                This will mark <b>every queued, running, and awaiting_human</b> job for org
                <span className="mx-1 font-mono">{target.org_id.slice(0, 12)}…</span>
                as <b>cancelled</b>. Currently active: <b>{target.active}</b>. This cannot be undone — impacted lodgements will need to be re-enqueued manually.
              </div>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Reason (optional — recorded in job history)</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                      rows={2} data-testid="fleet-cancel-reason"
                      placeholder="e.g. Firm requested pause during migration audit"
                      className="mt-1 text-[11px]" />
          </div>
          <label className="flex items-start gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
                   data-testid="fleet-cancel-ack"
                   className="mt-1" />
            <span>I understand this will cancel <b>{target.active}</b> job{target.active === 1 ? '' : 's'} and cannot be undone.</span>
          </label>
          {err && <div className="rounded-md bg-red-50 p-2 text-xs text-red-700" data-testid="fleet-cancel-error">{err}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Keep jobs</Button>
          <Button data-testid="fleet-cancel-submit" onClick={submit} disabled={busy || !ack}
                  className="bg-red-600 text-white hover:bg-red-700">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <X className="mr-1 h-4 w-4" />}
            Cancel {target.active} job{target.active === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ResumeDialog({ detail, onClose, onResumed }) {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [token, setToken] = React.useState('');
  const [humanInput, setHumanInput] = React.useState('');
  const [fullJob, setFullJob] = React.useState(null);

  React.useEffect(() => {
    if (!detail?.job) return;
    setToken(''); setHumanInput(''); setErr('');
    // load the full job to grab the resume_token (owned by same org — super_admin's org may differ)
    elodge.job(detail.job.id).then(setFullJob).catch(() => setFullJob(detail.job));
  }, [detail]);

  if (!detail) return null;
  const job = fullJob || detail.job;
  const suggestedToken = job.resume_token || '';

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      let payload = null;
      if (humanInput.trim()) {
        // accept either JSON or plain text (wrapped as {value})
        try { payload = JSON.parse(humanInput); }
        catch { payload = { value: humanInput.trim() }; }
      }
      await elodge.resume(job.id, token || suggestedToken, payload);
      onResumed?.();
      onClose?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Resume failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="fleet-resume-dialog">
        <DialogHeader><DialogTitle>Resume awaiting-human job</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md bg-amber-50 p-3 text-xs text-amber-800">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5" />
              <div>
                The worker paused for a CAPTCHA / MFA challenge. Enter the answer (e.g. the 6-digit code, or CAPTCHA text) below and the job will resume from where it left off.
              </div>
            </div>
          </div>
          <div className="rounded-md bg-slate-50 p-2 text-[11px] text-slate-600">
            <div><b>Matter:</b> {job.matter_ref}</div>
            <div><b>Subclass:</b> {job.subclass}</div>
            <div><b>Reason:</b> {job.error || '—'}</div>
          </div>
          {job.logs && job.logs.slice(-1)[0]?.screenshot && (
            <div>
              <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Latest worker screenshot</div>
              <img alt="worker" src={`data:image/jpeg;base64,${job.logs.slice(-1)[0].screenshot}`}
                   className="w-full rounded border border-slate-200" />
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Resume token</div>
            <input value={token || suggestedToken} onChange={(e) => setToken(e.target.value)}
                   data-testid="fleet-resume-token"
                   className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 font-mono text-[11px]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Human input</div>
            <Textarea value={humanInput} onChange={(e) => setHumanInput(e.target.value)}
                      rows={3} data-testid="fleet-resume-input"
                      placeholder='e.g. 723451 (MFA code) — or {"captcha":"ABCXYZ","mfa":"723451"}'
                      className="mt-1 font-mono text-[11px]" />
          </div>
          {err && <div className="rounded-md bg-red-50 p-2 text-xs text-red-700" data-testid="fleet-resume-error">{err}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="fleet-resume-submit" onClick={submit} disabled={busy || !(token || suggestedToken)}
                  className="bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Resume
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SlaTrendCard({ trend }) {
  if (!trend) {
    return (
      <Card className="mt-6 border-slate-200"><CardContent className="p-5" data-testid="fleet-sla-trend-loading">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
          <TrendingUp className="h-4 w-4 text-[#2ECC9A]" /> Lodgement SLA (p95) · rolling 7-day window
        </div>
        <div className="mt-3 flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
      </CardContent></Card>
    );
  }

  const subs = trend.subclasses || {};
  const subKeys = Object.keys(subs).sort();

  if (subKeys.length === 0) {
    return (
      <Card className="mt-6 border-slate-200"><CardContent className="p-5" data-testid="fleet-sla-trend-empty">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
          <TrendingUp className="h-4 w-4 text-[#2ECC9A]" /> Lodgement SLA (p95) · rolling 7-day window
        </div>
        <div className="mt-3 rounded-md border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500">
          Not enough finished lodgements over the last {trend.days || 90} days to plot an SLA trend yet.
        </div>
      </CardContent></Card>
    );
  }

  return (
    <Card className="mt-6 border-slate-200"><CardContent className="p-5" data-testid="fleet-sla-trend">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
          <TrendingUp className="h-4 w-4 text-[#2ECC9A]" /> Lodgement SLA (p95) · rolling 7-day window
        </div>
        <div className="text-[10px] uppercase tracking-wider text-slate-400">last {trend.days || 90} days</div>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {subKeys.map((subclass) => (
          <Sparkline key={subclass} subclass={subclass} series={subs[subclass]} />
        ))}
      </div>
    </CardContent></Card>
  );
}

function Sparkline({ subclass, series }) {
  const W = 260, H = 60, PAD = 4;
  const values = (series || []).map((p) => p.p95).filter((v) => v !== null && v !== undefined);
  const hasData = values.length >= 2;
  const min = hasData ? Math.min(...values) : 0;
  const max = hasData ? Math.max(...values) : 1;
  const range = Math.max(max - min, 1);
  const last = values.length ? values[values.length - 1] : null;
  const first = values.length ? values[0] : null;
  const trendDelta = (last !== null && first !== null) ? last - first : 0;
  const trendPct = (first && first > 0) ? Math.round((trendDelta / first) * 100) : 0;
  const trendTone = trendDelta <= 0 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-red-700 bg-red-50 border-red-200';
  const trendSymbol = trendDelta === 0 ? '·' : (trendDelta < 0 ? '↓' : '↑');

  const points = (series || []).map((p, i) => {
    const x = PAD + (i * (W - PAD * 2)) / Math.max((series.length - 1), 1);
    const v = p.p95;
    if (v === null || v === undefined) return null;
    const y = H - PAD - ((v - min) / range) * (H - PAD * 2);
    return { x, y, v, date: p.date };
  }).filter(Boolean);

  const pathD = points.length >= 2
    ? points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')
    : '';

  const areaD = points.length >= 2
    ? `${pathD} L${points[points.length - 1].x.toFixed(1)},${H - PAD} L${points[0].x.toFixed(1)},${H - PAD} Z`
    : '';

  return (
    <div data-testid={`fleet-sla-spark-${subclass}`} className="rounded-md border border-slate-100 bg-slate-50/60 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-xs font-semibold text-[#1A3A5C]">Subclass {subclass}</span>
        </div>
        {hasData ? (
          <Badge variant="outline" className={`text-[10px] ${trendTone}`}>
            {trendSymbol} {Math.abs(trendPct)}%
          </Badge>
        ) : (
          <Badge variant="outline" className="border-slate-200 text-[10px] text-slate-500">No data</Badge>
        )}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="mt-2 h-14 w-full" preserveAspectRatio="none" aria-hidden="true">
        {hasData ? (
          <>
            <path d={areaD} fill="rgba(46,204,154,0.14)" />
            <path d={pathD} fill="none" stroke="#2ECC9A" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
            {points.length > 0 && (
              <circle cx={points[points.length - 1].x} cy={points[points.length - 1].y} r="2.5" fill="#1A3A5C" />
            )}
          </>
        ) : (
          <line x1={PAD} y1={H / 2} x2={W - PAD} y2={H / 2} stroke="#e5e7eb" strokeDasharray="3 3" />
        )}
      </svg>
      <div className="mt-1 flex items-center justify-between text-[10px] text-slate-500">
        <span>{hasData ? `min ${Math.round(min)}m` : '—'}</span>
        <span className="font-semibold text-[#1A3A5C]">
          {last !== null ? `p95 ${Math.round(last)}m` : '—'}
        </span>
        <span>{hasData ? `max ${Math.round(max)}m` : '—'}</span>
      </div>
    </div>
  );
}
